import { useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";

import type { EnrichKind } from "@/hooks/useEnrich";
import { type SortKey, type SortState, DEFAULT_SORT, nextSort, sortRepos } from "@/lib/sort";
import { cn, freshness } from "@/lib/utils";
import type { Repo } from "@/types";

import { RateLimitBadge } from "./RateLimitBadge";

const COLS = "grid-cols-[1fr_60px_100px_110px_80px_120px]";
// Initial estimate only — real heights are measured per-row (measureElement) so a
// long description + wrapped topic chips don't clip/overlap into the next row (FE-3).
const ROW_EST = 96;

interface Busy {
  fullName: string;
  kind: EnrichKind;
}

function SortHeader({
  label,
  col,
  sort,
  onSort,
}: {
  label: string;
  col: SortKey;
  sort: SortState;
  onSort: (k: SortKey) => void;
}) {
  const active = sort.key === col;
  return (
    <button
      type="button"
      onClick={() => onSort(col)}
      className={cn("flex items-center gap-1 text-left hover:text-ink-1", active && "text-accent")}
    >
      {label}
      {active && <span>{sort.dir === "asc" ? "▲" : "▼"}</span>}
    </button>
  );
}

export function ResultsTable({
  repos,
  remaining,
  total,
  onAction,
  onFavorite,
  favPending,
  busy,
}: {
  repos: Repo[];
  remaining: number | null;
  total?: number | null;
  onAction?: (repo: Repo, kind: EnrichKind) => void;
  onFavorite?: (repo: Repo) => void;
  favPending?: boolean;
  busy?: Busy | null;
}) {
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const sorted = useMemo(() => sortRepos(repos, sort), [repos, sort]);
  const onSort = (k: SortKey) => setSort((s) => nextSort(s, k));

  const parentRef = useRef<HTMLDivElement>(null);
  const virt = useVirtualizer({
    count: sorted.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_EST,
    overscan: 12,
  });

  return (
    <div className="mt-4">
      <div className="mb-2 flex items-center justify-between text-xs text-ink-3">
        <span>
          {total != null && total > sorted.length
            ? `GitHub нашёл ${total.toLocaleString("ru")}, показаны первые ${sorted.length}`
            : `${sorted.length} репозиториев`}
        </span>
        {remaining != null && <RateLimitBadge remaining={remaining} />}
      </div>
      <div className="rounded-lg border border-line">
        <div
          className={`grid ${COLS} gap-2 border-b border-line bg-bg-1 px-3 py-2 text-xs font-medium text-ink-2`}
        >
          <SortHeader label="Репозиторий" col="full_name" sort={sort} onSort={onSort} />
          <SortHeader label="★" col="stars" sort={sort} onSort={onSort} />
          <span>Язык</span>
          <SortHeader label="Обновлён" col="pushed_at" sort={sort} onSort={onSort} />
          <span>Лицензия</span>
          <span />
        </div>
        <div ref={parentRef} className="max-h-[62vh] overflow-auto">
          <div style={{ height: virt.getTotalSize(), position: "relative" }}>
            {virt.getVirtualItems().map((vi) => {
              const r = sorted[vi.index];
              return (
                <div
                  key={r.full_name}
                  data-index={vi.index}
                  ref={virt.measureElement}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    transform: `translateY(${vi.start}px)`,
                  }}
                  className={`grid ${COLS} items-start gap-2 border-b border-line px-3 py-2 text-sm hover:bg-bg-1`}
                >
                  <div className="min-w-0">
                    <a
                      href={r.html_url ?? "#"}
                      target="_blank"
                      rel="noreferrer"
                      className="font-medium text-ink-1 hover:text-accent"
                    >
                      {r.full_name}
                    </a>
                    {r.description && (
                      <div className="line-clamp-2 text-xs text-ink-2">{r.description}</div>
                    )}
                    {r.topics.length > 0 && (
                      <div className="mt-0.5 flex flex-wrap gap-1">
                        {r.topics.slice(0, 6).map((t) => (
                          <span
                            key={t}
                            className="rounded bg-bg-2 px-1.5 py-0.5 text-[10px] text-ink-3"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <span className="text-ink-2">{r.stars ?? "—"}</span>
                  <span className="truncate text-ink-2">{r.language ?? "—"}</span>
                  <span className="text-ink-2">{freshness(r.pushed_at)}</span>
                  <span className="truncate text-ink-2">{r.license ?? "—"}</span>
                  <span className="flex gap-1">
                    {onAction && (
                      <>
                        <button
                          type="button"
                          onClick={() => onAction(r, "summary")}
                          disabled={busy?.fullName === r.full_name}
                          title="Суть репозитория (Haiku, рус.)"
                          className="rounded-md border border-line px-1.5 py-1 text-xs hover:border-accent hover:text-accent disabled:opacity-50"
                        >
                          {busy?.fullName === r.full_name && busy.kind === "summary" ? "…" : "📄"}
                        </button>
                        <button
                          type="button"
                          onClick={() => onAction(r, "ideas")}
                          disabled={busy?.fullName === r.full_name}
                          title="Сгенерировать идеи (Sonnet)"
                          className="rounded-md border border-line px-1.5 py-1 text-xs hover:border-accent hover:text-accent disabled:opacity-50"
                        >
                          {busy?.fullName === r.full_name && busy.kind === "ideas" ? "…" : "💡"}
                        </button>
                      </>
                    )}
                    {onFavorite && (
                      <button
                        type="button"
                        onClick={() => onFavorite(r)}
                        disabled={favPending}
                        title="В избранное"
                        className="rounded-md border border-line px-1.5 py-1 text-xs hover:border-accent hover:text-accent disabled:opacity-50"
                      >
                        ⭐
                      </button>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
